import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import CircularProgress from '@mui/material/CircularProgress';
import { changeActiveTab, setCurrentCourse } from '../../../../store/actions/appActions';
import { COURSES, SECTIONS } from '../../../../constants/courses';
import { CoursesModifications, CoursesButtonTypes } from '../../../../types/courses/courses';
import CoursePreview from '../CommonComponents/CoursePreview';
import buckleUpProposalBg from '../../../../assets/courses/previewCourses/enterAltitudeBg.png';
import { CursesSections } from '../../../../types/common';
import coursePlan from '../../../../assets/courses/enterAltitude/coursePlan.png';
import oneLesson from '../../../../assets/courses/enterAltitude/oneLesson.png';
import chatDecoration from '../../../../assets/courses/buckleUp/chatDecoration.png';
import ContactsMap from '../CommonComponents/ContactsMap';
import SideModal from '../../../UI/Modal/SideModal';
import { COLORS } from '../../../../constants/colors';
import useMediaQuery from '../../../../utils/breakpoints/useMediaQuery';
import PaymentSteps from '../../../PaymentSteps/PaymentSteps';
import { selectUserProfile } from '../../../../store/selectors/user';
import { Button } from '../../../UI/Button';
import Puzzle1 from '../../../../assets/courses/mentalMath/pazzle1.svg';
import Puzzle2 from '../../../../assets/courses/mentalMath/pazzle2.svg';
import Puzzle3 from '../../../../assets/courses/mentalMath/pazzle3.svg';
import Puzzle4 from '../../../../assets/courses/mentalMath/pazzle4.svg';

import { downloadMaterials } from '../../../../utils/courses/downloadMaterials';
import {
   RestoreWrapper,
   CourseTitle,
   CoursePlanWrapper,
   ChatDecorationWrapper,
   ChatSubtitle,
   ChatTitle,
   ButtonWrapper,
   PuzzleContainer,
   GridItem1,
   GridItem2,
   GridItem4,
   GridItem5,
   GridItem8,
   PuzzleImg,
   PuzzleText,
   GridItemPuzzle,
   CourseSubtitle,
   CourseParagraphTitle
} from './styles';

const EnterAltitudeProposal = () => {
   const [isModalOpen, setIsModalOpen] = useState(false);
   const dispatch = useDispatch();
   const { t } = useTranslation();
   const navigate = useNavigate();
   const { isMobile, width } = useMediaQuery()
   const userProfileInfo = useSelector(selectUserProfile)
   const { enqueueSnackbar } = useSnackbar();
   const hasUserPaid = userProfileInfo?.premium_courses?.includes(COURSES.enterAltitude)
   const [isDownloadingDone, setIsDownloadDone] = useState(false);
   const [isDownloading, setIsDownloading] = useState(false);


   const PuzzleComponent = ({ title, subtitle, decoration }: { title: string; subtitle: string; decoration: string }) => {
      return (
         <GridItemPuzzle decoration={decoration}>
            <CourseParagraphTitle>{t(`course.${COURSES.enterAltitudeProposal}.${title}`)}</CourseParagraphTitle>
            <CourseSubtitle>{t(`course.${COURSES.enterAltitudeProposal}.${subtitle}`)}</CourseSubtitle>
         </GridItemPuzzle>
      );
   };

   const handleButtonClick = () => {
      if (hasUserPaid) {
         dispatch(changeActiveTab({ activeTab: 'cabinet' }))
         dispatch(setCurrentCourse({ courseModification: COURSES.enterAltitude as CoursesModifications }))
         navigate('/cabinet')
      } else {
         handleOpenModal()
      }
   }

   const handleOpenModal = () => {
      if (userProfileInfo?.email) {
         setIsModalOpen(!isModalOpen);
      } else {
         enqueueSnackbar(t('course.enterAltitudeProposal.modalError'), {
            variant: 'warning', anchorOrigin: {
               vertical: 'top',
               horizontal: 'left'
            }
         })
      }
   }

   const handleCloseModal = () => {
      setIsModalOpen(false);
   }

   const handleMaterialsClick = () => {
      setIsDownloading(true);
      return downloadMaterials("https://s3.amazonaws.com/urbancenter.md/enterAltitude/lesson3/lesson3.pdf", ["Lesson_demo.pdf"], () => {
         setIsDownloading(false)
         setIsDownloadDone(true)
      })
   }

   const renderDownloadButton = () => {
      if (isDownloading) {
         return <CircularProgress size={20} color='secondary' />
      }
      if (isDownloadingDone) {
         return <DownloadDoneIcon fontSize='small' />
      } else {
         return t(`course.enterAltitudeProposal.demoLesson`)
      }
   }


   useEffect(() => {
      dispatch(changeActiveTab({ activeTab: 'cabinet' }))
      if (hasUserPaid) {
         dispatch(setCurrentCourse({ courseModification: COURSES.enterAltitude as CoursesModifications }))
         return
      }
      dispatch(setCurrentCourse({ courseModification: COURSES.notSelected as CoursesModifications }))
   }, []);

   return (
      <RestoreWrapper>
         <CoursePreview
            bgImage={buckleUpProposalBg}
            sectionName={SECTIONS.kids as CursesSections}
            modification={COURSES.enterAltitudeProposal as CoursesModifications}
            button={hasUserPaid ? CoursesButtonTypes.READ : CoursesButtonTypes.BUY}
            buttonModification={'primaryOutlined'}
            customTag={'300 MDL'}
            handleButtonClick={handleButtonClick}
         />
         <CourseTitle>
            {t('course.enterAltitudeProposal.howTo')}
         </CourseTitle>
         <CoursePlanWrapper isMobile={isMobile}>
            <img src={coursePlan} alt="course plan" />
            <div>
               <CourseTitle>
                  {t(`course.${COURSES.enterAltitudeProposal}.lessonTitle`)}
               </CourseTitle>
               <p>
                  {t(`course.${COURSES.enterAltitudeProposal}.lessonSubtitle`)}
               </p>
            </div>
         </CoursePlanWrapper>
         <CoursePlanWrapper isMobile={isMobile}>
            <div>
               <CourseTitle>
                  {t(`course.${COURSES.enterAltitudeProposal}.lessonTitleTwo`)}
               </CourseTitle>
               <p>
                  {t(`course.${COURSES.enterAltitudeProposal}.lessonSubtitleTwo`)}
               </p>
            </div>
            <img src={oneLesson} alt="course plan" />
         </CoursePlanWrapper>
         <ChatDecorationWrapper isMobile={isMobile} isSmallScreen={width < 1400}>
            <ChatTitle isMobile={isMobile} isSmallScreen={width < 1400}>
               {t(`course.${COURSES.enterAltitudeProposal}.includedTitle`)}
            </ChatTitle>
            <ChatSubtitle isMobile={isMobile} isSmallScreen={width < 1400}>
               <div>
                  1: {t(`course.${COURSES.enterAltitudeProposal}.includedDescriptionOne`)}
               </div>
               <div>
                  2: {t(`course.${COURSES.enterAltitudeProposal}.includedDescriptionTwo`)}
               </div>
               <div>
                  3: {t(`course.${COURSES.enterAltitudeProposal}.includedDescriptionThree`)}
               </div>
               <div>
                  4: {t(`course.${COURSES.enterAltitudeProposal}.includedDescriptionFour`)}
               </div>
               <div>
                  5: {t(`course.${COURSES.enterAltitudeProposal}.includedDescriptionFive`)}
               </div>
            </ChatSubtitle>
            <img src={chatDecoration} alt="" />
            <ButtonWrapper isMobile={isMobile} isSmallScreen={width < 1400}>
               <Button onClick={handleButtonClick} modifier='primaryOutlined' color={SECTIONS.kids as CursesSections}>{t(`course.buy`)}</Button>
               <Button
                  onClick={handleMaterialsClick}
                  modifier='primaryOutlined'
                  color={SECTIONS.kids as CursesSections}
               >
                  {renderDownloadButton()}
               </Button>
            </ButtonWrapper>
         </ChatDecorationWrapper>
         <div>
            <CourseTitle>{t(`course.${COURSES.enterAltitudeProposal}.buildFutureTitle`)}</CourseTitle>
            <PuzzleContainer isMobile={isMobile}>
               <GridItem1 isMobile={isMobile}>
                  <PuzzleText>{t(`course.${COURSES.enterAltitudeProposal}.buildFutureSubtitle`)}</PuzzleText>
               </GridItem1>
               <GridItem2>
                  <PuzzleImg src={Puzzle2} isMobile={isMobile}></PuzzleImg>
               </GridItem2>
               <PuzzleComponent title='thirdPuzleTitle' subtitle='thirdPuzleDescription' decoration='three' />
               <GridItem4 isMobile={isMobile}>
                  <PuzzleImg src={Puzzle4} isMobile={isMobile}></PuzzleImg>
               </GridItem4>
               <GridItem5>
                  <PuzzleImg src={Puzzle1} isMobile={isMobile}></PuzzleImg>
               </GridItem5>
               <PuzzleComponent title='firstPuzleTitle' subtitle='firstPuzleDescription' decoration='one' />
               <PuzzleComponent title='secondPuzleTitle' subtitle='secondPuzleDescription' decoration='two' />
               <GridItem8>
                  <PuzzleImg src={Puzzle3} isMobile={isMobile}></PuzzleImg>
               </GridItem8>
               <PuzzleComponent title='fourthPuzleTitle' subtitle='fourthPuzleDescription' decoration='four' />
            </PuzzleContainer>
         </div>
         <ContactsMap handleButtonClick={handleOpenModal} />
         <SideModal
            open={isModalOpen}
            handleClose={handleCloseModal}
            bgcolor={COLORS.courseCardBackground}
            modalWidth={500}
            height={500}
         >
            <PaymentSteps
               isMaib={true}
               modification={SECTIONS.kids as CursesSections}
               course={COURSES.enterAltitudeProposal as CoursesModifications}
               handleCloseModal={handleCloseModal}
            />
         </SideModal>
      </RestoreWrapper>
   );
};

export default EnterAltitudeProposal;